// Core type definitions for Fancy Hands

export interface User {
  key: string;
  uuid: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  primary_email: string;
  primary_email_confirmed: boolean;
  tasks_remaining: number;
  verified_uuid: string | null; // Used for realtime socket identification only
  has_calendar: boolean;
  is_beta: boolean; // True if user is an assistant
  is_manager: boolean; // True if user is a manager
  date_created: string;
  // Claim limits (assistants). 0 = default cap.
  allowed_open_tasks?: number;
  date_next_claim?: string | null;
}

export interface Identity {
  key: string;
  uuid: string;
  user: string;
  type: 'customer' | 'assistant' | 'support';
  verified: boolean;
  date_created: string;
}

export interface FHRequest {
  key: string;
  uuid: string;
  datastore_key: string;
  title: string;
  content: string;
  content_type: string;
  status: number;
  claimed: boolean;
  claimed_by: string | null;
  date_created: string;
  date_updated: string;
  project: string | null;
  price: number;
  user_name: string | null;
  // Assistant/Mentor info
  assistant_name?: string | null;
  assistant_uuid?: string | null;
  mentor_name?: string | null;
  mentor_uuid?: string | null;
  // Pricing-related fields
  task_credits?: number;
  solver_paid?: boolean;
  solver_paid_amount?: number;
  solver_paid_date?: string | null;
  // Mentor-related fields
  mentor_claimed?: boolean;
  mentor?: string | null;  // Key of mentor who claimed
  mentor_claim_date?: string | null;  // When claim was made (for auto-expire)
  needs_mentor_attention?: boolean;
  needs_manager_attention?: boolean;
  mentor_deny_count?: number;
  // Archive flag (assistant/spam archive, not the client's)
  archived?: boolean;
  // Client archive: hidden from the client's default request list
  client_archived?: boolean;
  // Incoming email address for this request
  incoming_email?: string | null;
  // Internal manager note (visible to assistants, not clients)
  god_note?: string | null;
  // Star rating (1-5, set by client)
  star_rating?: number | null;
  // Voice request (created by phone) flag
  is_voice_request?: boolean;
  // Recurring task fields
  is_recurring?: boolean;
  recurring_info?: FHRecurringInfo | null;
}

export interface FHRecurringInfo {
  key: string;
  uuid: string;
  frequency: string;
  next_date: string | null;
  active: boolean;
  skip: boolean;
  title: string;
  content: string;
  weekday: number | null;
  weekday_name: string | null;
  day_of_month: number | null;
  date_created: string | null;
}

export interface PendingResponse {
  body: string;
  date_created: string | null;
  author_name: string | null;
  suggested_status: number | null;
  close_without_response?: boolean;
  delay_time?: string | null;
}

export interface FHRequestMessage {
  key: string;
  uuid: string;
  request: string;
  content: string;
  content_type?: 'text/plain' | 'text/html';
  message_type: 'client' | 'helper' | 'assistant' | 'system' | 'mentor' | 'third-party';
  author: 'user' | 'system' | 'openai-gpt3' | 'manager';
  author_name?: string | null;
  date_created: string;
  client_viewable: boolean;
  // Email metadata (present on messages from incoming emails, including client and third-party)
  subject?: string | null;
  sender?: string | null;
  original_to?: string | null;
  original_cc?: string | null;
}

// Request status constants (from backend request.py)
export const REQUEST_STATUS = {
  NEW: 1,
  AWAITING_MENTOR_APPROVAL: 3,
  MENTOR_RESPONSE: 4,
  OPEN: 5,
  AWAITING_RESPONSE: 7,
  CLIENT_RESPONSE: 10,
  AWAITING_THIRD_PARTY: 11,
  AWAITING_MANAGER_RESPONSE: 12,
  THIRD_PARTY_RESPONSE: 13,
  CLOSED: 20,
  EXPIRED: 21,
  ARCHIVED: 22,
} as const;

// API response types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface PagedResponse<T> {
  items: T[];
  cursor: string | null;
  has_more: boolean;
}

export interface AuthToken {
  token: string;
  user: User;
}

export interface AdminPayment {
  key: string;
  date_created: string | null;
  name: string;
  base_pay: number;
  bonus: number;
  total: number;
  status: string | null;
  accepted: boolean;
  settled: boolean;
  date_settled: string | null;
  request_count: number;
}

export interface PaymentRequestSummary {
  key: string;
  uuid: string | null;
  title: string;
  solver_paid_amount?: number;
  mentor_price?: number;
  date_solved: string | null;
}

export interface PaymentRequestsResponse {
  tasks: PaymentRequestSummary[];
  mentor_reviews: PaymentRequestSummary[];
}

// User Settings types
export interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  timezone_city: string | null;
  home_street_1: string | null;
  home_street_2: string | null;
  home_city: string | null;
  home_state: string | null;
  home_zip: string | null;
  home_country: string | null;
  work_street_1: string | null;
  work_street_2: string | null;
  work_city: string | null;
  work_state: string | null;
  work_zip: string | null;
}

export interface EmailPreferences {
  send_task_autoresponse_email: boolean;
  allow_weekly_email: boolean;
  looking_ahead_frequency: 'monday' | 'everyday';
  promote_referral_code: boolean;
}

export interface UserSettings {
  profile: UserProfile;
  email_preferences: EmailPreferences;
}

// An address on the account (/users/email/list). Pending ones are waiting for
// their verification link and don't match inbound mail or sign in.
export interface EmailAddress {
  email: string;
  status: 'verified' | 'pending';
  is_primary: boolean;
  expired?: boolean;
}

export interface EmailAddressList {
  items: EmailAddress[];
}

export interface AdminUserSettings extends UserSettings {
  user_uuid: string;
}

export interface ShiftHistoryItem {
  key: string;
  uuid: string;
  shift?: string | null; // legacy: older activity entries were keyed by shift
  associated_type: string; // e.g., "clock:start", "request:claim", "support:email-search"
  associated_uuid: string;
  collab?: string;
  request?: string;
  date_created: string;
  date_updated: string;
  // User info (populated by backend to_json_hook)
  user_uuid?: string;
  user_name?: string;
}

export interface AccountSummary {
  tasks_remaining: number;
  is_unlimited: boolean;
  rollover: number;
  next_billing_cycle_date: string | null;
}

export interface BillingInfo {
  subscription_service: 'stripe' | 'recurly';
  has_billing: boolean;
  billing_portal_url: string | null;
}

// Dwolla types for assistant payouts
export interface DwollaStatus {
  has_customer: boolean;
  has_funding_source: boolean;
}

export interface DwollaSource {
  id: string;
  name: string;
  type: string;
  bank_account_type: string;
  bank_name: string | null;
  created: string;
  _links: Record<string, { href: string }>;
}

export interface DwollaTransaction {
  id: string;
  status: string;
  amount: string;
  currency: string;
  created: string;
  _links: Record<string, { href: string }>;
}

// Forum types
export interface ForumSection {
  key: string;
  uuid: string;
  title: string;
  description: string;
  slug: string;
  order: number;
  date_created: string;
  date_updated: string;
}

export interface ForumPost {
  key: string;
  uuid: string;
  title: string;
  body: string;
  slug: string;
  sticky: boolean;
  label: string | null;
  edited: boolean;
  archived: boolean;
  author_name: string;
  user_uuid?: string;
  comment_count: number;
  is_owner: boolean;
  date_created: string;
  date_updated: string;
}

export interface ForumComment {
  key: string;
  uuid: string;
  body: string;
  slug: string;
  likes: number;
  edited: boolean;
  archived: boolean;
  author_name: string;
  user_uuid?: string;
  is_owner: boolean;
  user_has_liked: boolean;
  date_created: string;
  date_updated: string;
}

export interface ForumActivity {
  key: string;
  uuid: string;
  activity_type: 'post' | 'comment';
  activity_date: string;
  author_name: string;
  user_uuid?: string;
  archived?: boolean;
  // Post fields
  title?: string;
  body?: string;
  slug?: string;
  sticky?: boolean;
  label?: string | null;
  comment_count?: number;
  // Comment fields
  likes?: number;
  post_title?: string;
  post_slug?: string;
  section_slug?: string;
}

export interface ForumSectionWithActivity extends ForumSection {
  recent_activity: ForumActivity[];
}

// Field Guide types
export interface FieldguideSection {
  key: string;
  uuid: string;
  name: string;
  slug: string;
  order: number;
  deleted: boolean;
  guide_type: string;
  date_created: string;
  date_updated: string;
}

export interface FieldguideEntry {
  key: string;
  uuid: string;
  title: string;
  slug: string;
  content: string;
  content_type: string;  // 'text/html' or 'text/markdown'
  order: number;
  deleted: boolean;
  section: string;  // section key
  modified_by_name: string;
  date_created: string;
  date_updated: string;
}

export interface RequestDelay {
  uuid: string;
  status: 'scheduled' | 'fired' | 'canceled' | 'closed';
  scheduled_time: string;
  date_created: string;
  // Event dates
  fired_date?: string | null;
  canceled_date?: string | null;
}

export interface DelayTimelineEvent {
  uuid: string;
  event_type: 'scheduled' | 'fired' | 'canceled';
  date: string;  // ISO date string for when this event occurred
  scheduled_time?: string;  // For context on "scheduled" events
  delay_uuid: string;
}

// Timeline types for mixed message + email + delay event display
import type { Email } from '../api/email_api';

export type TimelineItem =
  | { kind: 'message'; data: FHRequestMessage }
  | { kind: 'outgoing_email'; data: Email }
  | { kind: 'delay_event'; data: DelayTimelineEvent };
